/**
 * @fileoverview Define shared queue filtering, preference, and lead-preview semantics used by `kc-internal-frontend/src/components/Intake/IntakeQueue.js` and batch views. Backend lead/pipeline records remain authoritative; helpers adapt their state for columns, flags, and previews. Saved table preferences affect presentation rather than lead decisions or execution eligibility.
 */

import {
  formatDate,
  getDuplicateStatusClass,
  getDuplicateStatusLabel,
  getPromotionBatchStatusClass,
  getPromotionBatchStatusLabel
} from '../intakeFormatters';
import { ACTIVE_STATUSES, CLEARED_STATUSES, COMPLETE_STATUSES, FAILED_RUN_STATUSES, REVIEW_RUN_STATUSES } from '../intakeStatuses';

export const DEFAULT_FILTERS = {
  bucket: 'all',
  search: '',
  status: [],
  dedupe: 'all',
  stage: [],
  category: 'all',
  state: 'all',
  defendant: '',
  assigned_owner: 'all',
  source: 'all',
  staff_review_required: 'all',
  potential_class_action: 'all',
  outreach_email_sent: 'all',
  final_decision: 'all',
  created_start: '',
  created_end: '',
  updated_start: '',
  updated_end: '',
  next_action_due: ''
};

export const FALLBACK_STATUSES = [
{ label: 'New', value: 'new' },
{ label: 'Processing', value: 'processing' },
{ label: 'Waiting on Prospect', value: 'waiting_on_prospect' },
{ label: 'Needs Staff Review', value: 'needs_staff_review' },
{ label: 'Ready for Decision', value: 'ready_for_decision' },
{ label: 'Accepted', value: 'accepted' },
{ label: 'Rejected', value: 'rejected' },
{ label: 'Converted', value: 'converted' },
{ label: 'Duplicate', value: 'duplicate' },
{ label: 'Archived', value: 'archived' },
{ label: 'Error', value: 'error' }];


export const FALLBACK_STAGES = [
{ label: 'Ingest and Dedupe', value: 'ingest_dedupe' },
{ label: 'Triage', value: 'triage' },
{ label: 'Specialist Intake Review', value: 'specialist_intake_review' },
{ label: 'Class Action Detection', value: 'class_action_detection' },
{ label: 'Information Gathering', value: 'info_gathering' },
{ label: 'Information Request', value: 'information_request' },
{ label: 'Document Review', value: 'document_review' },
{ label: 'Information Evaluation', value: 'information_evaluation' },
{ label: 'Specialist Claim Evaluation', value: 'specialist_evaluation' },
{ label: 'Final Brief', value: 'final_brief' },
{ label: 'Decision', value: 'decision' },
{ label: 'Conversion', value: 'conversion' }];


export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
export const COLUMNS_KEY = 'intake_table_columns';
export const COLUMNS_VERSION_KEY = 'intake_table_columns_version';
export const COLUMNS_VERSION = '5';
export const TABLE_STATE_KEY = 'intake_table_state';
export const TABLE_STATE_VERSION_KEY = 'intake_table_state_version';
export const TABLE_STATE_VERSION = '2';
export const LEAD_RECEIVED_SORT_FIELD = 'lead_received_at';

export const INTAKE_COLUMNS = [
{
  key: 'lead',
  label: 'Lead',
  sortField: 'prospect_name',
  width: '14rem',
  filter: { field: 'search', type: 'search', placeholder: 'Name, email, phone' }
},
{
  key: 'next_action',
  label: 'Next Action',
  sortField: 'next_action_at',
  width: '11rem',
  filter: { field: 'next_action_due', type: 'date', label: 'Due by' }
},
{
  key: 'status',
  label: 'Status',
  sortField: 'status',
  width: '10rem',
  filter: { field: 'status', type: 'multiselect', optionsKey: 'statuses' }
},
{
  key: 'stage',
  label: 'Stage',
  sortField: 'current_stage',
  width: '11rem',
  filter: { field: 'stage', type: 'multiselect', optionsKey: 'stages' }
},
{
  key: 'review',
  label: 'Staff Review',
  width: '7.5rem',
  filter: { field: 'staff_review_required', type: 'select', optionsKey: 'staff_review_required' }
},
{
  key: 'class_action',
  label: 'Class Action',
  width: '8rem',
  filter: { field: 'potential_class_action', type: 'select', optionsKey: 'potential_class_action' }
},
{
  key: 'category',
  label: 'Category',
  sortField: 'category',
  width: '10.5rem',
  filter: { field: 'category', type: 'select', optionsKey: 'categories' }
},
{
  key: 'defendant',
  label: 'Defendant',
  sortField: 'defendant',
  width: '14rem',
  filter: { field: 'defendant', type: 'text', placeholder: 'Defendant' }
},
{
  key: 'state',
  label: 'State',
  width: '6rem',
  filter: { field: 'state', type: 'select', optionsKey: 'states' }
},
{
  key: 'documents',
  label: 'Docs',
  sortField: 'document_count',
  width: '6rem'
},
{
  key: 'last_inbound',
  label: 'Last Inbound',
  sortField: 'last_inbound_contact_at',
  width: '10.5rem'
},
{
  key: 'owner',
  label: 'Owner',
  sortField: 'assigned_owner',
  width: '13rem',
  filter: { field: 'assigned_owner', type: 'select', optionsKey: 'assigned_owners' }
},
{
  key: 'updated',
  label: 'Updated',
  sortField: 'updated_at',
  width: '10.5rem',
  filter: { type: 'dateRange', startField: 'updated_start', endField: 'updated_end' }
},
{
  key: 'dedupe',
  label: 'Dedupe',
  width: '11rem',
  filter: { field: 'dedupe', type: 'select', optionsKey: 'dedupe' }
},
{
  key: 'source',
  label: 'Source',
  sortField: 'source',
  width: '9rem',
  filter: { field: 'source', type: 'select', optionsKey: 'sources' }
},
{
  key: 'pipedrive',
  label: 'Pipedrive',
  width: '10.5rem'
},
{
  key: 'round',
  label: 'Round',
  width: '5.5rem'
},
{
  key: 'decision',
  label: 'Decision',
  width: '9.5rem',
  filter: { field: 'final_decision', type: 'select', optionsKey: 'final_decisions' }
},
{
  key: 'created',
  label: 'Created',
  sortField: 'created_at',
  width: '10.5rem',
  filter: { type: 'dateRange', startField: 'created_start', endField: 'created_end' }
}];


export const DEFAULT_COLUMN_KEYS = INTAKE_COLUMNS.map((column) => column.key);
export const ACTION_COLUMN_WIDTH = '11.5rem';
export const DEFAULT_QUEUE_VIEW = 'worklist';
export const BOARD_PAGE_SIZE = 200;
export const BUCKET_FILTERS = [
{ key: 'all', label: 'Open', icon: 'pi pi-folder-open' },
{ key: 'resolved', label: 'Archived & Rejected', icon: 'pi pi-box' },
{ key: 'needs_attention', label: 'Needs Attention', icon: 'pi pi-bell' },
{ key: 'needs_review', label: 'Needs Review', icon: 'pi pi-exclamation-circle' },
{ key: 'duplicate_review', label: 'Duplicate Review', icon: 'pi pi-clone' },
{ key: 'ready_for_decision', label: 'Ready for Decision', icon: 'pi pi-check-circle' },
{ key: 'document_follow_up', label: 'Document Follow-up', icon: 'pi pi-file' },
{ key: 'running', label: 'Running', icon: 'pi pi-spin pi-spinner' },
{ key: 'errors', label: 'Errors', icon: 'pi pi-times-circle' }];
export const BUCKET_LABELS = BUCKET_FILTERS.reduce((labels, bucket) => ({
  ...labels,
  [bucket.key]: bucket.label
}), {});
export const QUEUE_SORT_OPTIONS = [
{ label: 'Lead Received', value: LEAD_RECEIVED_SORT_FIELD, defaultOrder: -1 },
{ label: 'Updated', value: 'updated_at', defaultOrder: -1 },
{ label: 'Lead Created', value: 'created_at', defaultOrder: -1 },
{ label: 'Next Action', value: 'next_action_at', defaultOrder: 1 },
{ label: 'Lead Name', value: 'prospect_name', defaultOrder: 1 },
{ label: 'Stage', value: 'current_stage', defaultOrder: 1 },
{ label: 'Status', value: 'status', defaultOrder: 1 },
{ label: 'Owner', value: 'assigned_owner', defaultOrder: 1 },
{ label: 'Documents', value: 'document_count', defaultOrder: -1 }];

export const CLOSED_INTAKE_STATUSES = new Set(['converted', 'accepted', 'engagement_letter_to_be_sent', 'rejected', 'duplicate', 'archived']);
export const RESOLVED_INTAKE_PRESENTATION_STATUSES = new Set(['converted', 'rejected', 'archived']);

// Presentation follows the operational lifecycle, not whichever historical
// pipeline fields happen to remain on the record after it closes.


export function getLeadPresentationState(lead, { hasPendingAttorneyReview = false } = {}) {
  // A duplicate can resume after a merge, and accepted/engagement records may
  // still need operational follow-through. They are not presentation-final.
  if (RESOLVED_INTAKE_PRESENTATION_STATUSES.has(lead?.status)) return 'resolved';
  if (lead?.status === 'ready_for_decision' && lead?.next_action_label === 'Attorney review pending' && hasPendingAttorneyReview) return 'awaiting_attorney_review';
  return 'active';
}



export function isResolvedIntakeLead(lead) {
  return getLeadPresentationState(lead) === 'resolved';
}
export const RUN_REMAINING_STOP_STAGES = new Set(['decision', 'conversion']);
export const RUN_REMAINING_ACTIVE_RESULT_STATUSES = new Set(['started', 'running', 'processing', 'queued', 'waiting']);
export const ACTIVE_PREVIEW_REFRESH_INTERVAL_MS = 10000;
export const NOT_VIABLE_FINAL_RECOMMENDATION_TOKENS = new Set([
'not_viable',
'not_viable_case',
'non_viable',
'non_viable_case',
'no_viable_case',
'reject',
'rejected',
'auto_reject',
'auto_rejected']);
export const EMPTY_ACTIVE_PIPELINE_WORK = {
  pipeline_runs: 0,
  stage_runs: 0,
  agent_runs: 0,
  total: 0
};
export const SORT_FIELDS = new Set(
  INTAKE_COLUMNS
  .map((column) => column.sortField)
  .filter(Boolean)
  .concat(QUEUE_SORT_OPTIONS.map((option) => option.value))
);
export const DEFAULT_TABLE_STATE = {
  filters: DEFAULT_FILTERS,
  page: 1,
  pageSize: 25,
  sortField: LEAD_RECEIVED_SORT_FIELD,
  sortOrder: -1
};
export const COMPACT_CELL_COLUMN_KEYS = new Set([
'status',
'dedupe',
'stage',
'state',
'source',
'pipedrive',
'documents',
'next_action',
'last_inbound',
'round',
'review',
'class_action',
'decision',
'created',
'updated']);

export const FILTER_LABELS = {
  bucket: 'View',
  search: 'Search',
  status: 'Status',
  dedupe: 'Dedupe',
  stage: 'Stage',
  category: 'Category',
  state: 'State',
  defendant: 'Defendant',
  assigned_owner: 'Owner',
  source: 'Source',
  staff_review_required: 'Staff Review',
  potential_class_action: 'Class Action',
  outreach_email_sent: 'Emailed',
  final_decision: 'Decision',
  created_start: 'Created From',
  created_end: 'Created To',
  updated_start: 'Updated From',
  updated_end: 'Updated To',
  next_action_due: 'Next Action Due'
};

export const MULTI_SELECT_FILTER_FIELDS = new Set(['status', 'stage']);

export const STATIC_FILTER_OPTIONS = {
  staff_review_required: [
  { label: 'All', value: 'all' },
  { label: 'Required', value: 'true' },
  { label: 'Not Required', value: 'false' }],
  potential_class_action: [
  { label: 'All', value: 'all' },
  { label: 'Potential Class Action', value: 'true' },
  { label: 'Not Tagged', value: 'false' }],
  outreach_email_sent: [
  { label: 'All', value: 'all' },
  { label: 'Email Sent', value: 'true' },
  { label: 'No Email Sent', value: 'false' }],
  dedupe: [
  { label: 'All', value: 'all' },
  { label: 'Likely Duplicate', value: 'likely_duplicate' },
  { label: 'Possible Duplicate', value: 'possible_duplicate' },
  { label: 'Duplicate', value: 'confirmed_duplicate' },
  { label: 'Merged', value: 'merged' },
  { label: 'No Duplicate / Cleared', value: 'not_duplicate' }]

};



export function titleize(value) {
  if (!value) return '-';
  return String(value)
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');
}



export function normalizeRecommendationToken(value) {
  return String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');
}



export function isNotViableFinalRecommendation(value) {
  return NOT_VIABLE_FINAL_RECOMMENDATION_TOKENS.has(normalizeRecommendationToken(value));
}



export function formatRecommendationDecision(value) {
  if (!value) return '-';
  if (isNotViableFinalRecommendation(value)) return 'Not Viable';
  return titleize(normalizeRecommendationToken(value));
}



export function getDecisionText(row) {
  if (row.final_decision) return titleize(row.final_decision);
  const recommendation = row.latest_final_recommendation || {};
  const classification = recommendation.classification || row.final_recommendation_classification;
  if (classification) return formatRecommendationDecision(classification);
  return formatRecommendationDecision(recommendation.recommended_action || row.final_recommendation_action);
}



export function formatTableDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const stringValue = String(value);
  const hasTime = /T|\d{1,2}:\d{2}/.test(stringValue);
  const dateOptions = {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit'
  };
  if (!hasTime) {
    return date.toLocaleDateString(undefined, dateOptions);
  }
  return date.toLocaleString(undefined, {
    ...dateOptions,
    hour: 'numeric',
    minute: '2-digit'
  });
}



export function normalizeOptions(values) {
  return (values || []).filter(Boolean).map((value) => {
    if (typeof value === 'object') {
      return {
        label: value.label || titleize(value.value),
        value: value.value
      };
    }

    return { label: String(value), value };
  });
}



export function optionList(values) {
  return [{ label: 'All', value: 'all' }].concat(normalizeOptions(values));
}



export function getOptionLabel(values, selectedValue) {
  const option = optionList(values).find((item) => String(item.value) === String(selectedValue));
  return option?.label || String(selectedValue);
}



export function getSelectedOptionLabels(values, selectedValues) {
  const selections = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
  return selections
  .filter((value) => value && value !== 'all')
  .map((value) => getOptionLabel(values, value))
  .join(', ');
}



export function getStatusClass(status) {
  switch (status) {
    case 'new':
      return 'intake-badge--info';
    case 'processing':
    case 'waiting_on_prospect':
      return 'intake-badge--warning';
    case 'needs_staff_review':
    case 'error':
      return 'intake-badge--danger';
    case 'ready_for_decision':
    case 'accepted':
    case 'converted':
      return 'intake-badge--success';
    case 'rejected':
    case 'duplicate':
    case 'archived':
      return 'intake-badge--muted';
    default:
      return 'intake-badge--info';
  }
}



export function getStageClass(stage) {
  switch (stage) {
    case 'ingest_dedupe':
      return 'intake-badge--warning';
    case 'triage':
      return 'intake-badge--cyan';
    case 'analysis':
    case 'specialist_intake_review':
    case 'specialist_evaluation':
      return 'intake-badge--blue';
    case 'class_action_detection':
      return 'intake-badge--violet';
    case 'info_gathering':
    case 'information_request':
    case 'information_evaluation':
      return 'intake-badge--sky';
    case 'document_review':
      return 'intake-badge--violet';
    case 'final_brief':
      return 'intake-badge--indigo';
    case 'decision':
      return 'intake-badge--teal';
    case 'conversion':
      return 'intake-badge--success';
    default:
      return 'intake-badge--muted';
  }
}



export function getSortIconClass(active, sortOrder) {
  if (!active) return 'pi pi-sort-alt';
  return sortOrder === -1 ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up';
}



export function getDefaultSortOrder(field) {
  return QUEUE_SORT_OPTIONS.find((option) => option.value === field)?.defaultOrder || -1;
}



export function getSortDirectionLabel(sortOrder, sortField) {
  const isDateSort = [LEAD_RECEIVED_SORT_FIELD, 'created_at', 'updated_at', 'next_action_at', 'last_inbound_contact_at'].includes(sortField);
  if (isDateSort) {
    return sortOrder === -1 ? 'Newest first' : 'Oldest first';
  }
  return sortOrder === -1 ? 'Descending' : 'Ascending';
}



export function getPipedriveText(row) {
  const parts = [];
  if (row.pipedrive_lead_id) parts.push(`Lead ${row.pipedrive_lead_id}`);
  if (row.pipedrive_deal_id) parts.push(`Deal ${row.pipedrive_deal_id}`);
  if (row.pipedrive_person_id) parts.push(`Person ${row.pipedrive_person_id}`);
  return parts.length ? parts.join(' / ') : '-';
}



export function getCellClassName(column) {
  return COMPACT_CELL_COLUMN_KEYS.has(column.key) ? 'intake-table__cell--compact' : 'intake-table__cell';
}



export function getLeadStageLabel(lead) {
  if (lead?.status === 'converted') return 'Intake completed';
  return lead?.current_stage ? titleize(lead.current_stage) : '—';
}



export function getCellText(row, column) {
  switch (column.key) {
    case 'lead':
      return row.prospect_name || row.lead_title || '-';
    case 'status':
      return titleize(row.status);
    case 'dedupe':
      return getDuplicateStatusLabel(row);
    case 'stage':
      return getLeadStageLabel(row);
    case 'category':
      return titleize(row.category);
    case 'defendant':
      return row.defendant_name_raw || row.defendant_name_normalized || '-';
    case 'state':
      return row.state_client || row.state_defendant || row.state_events || '-';
    case 'source':
      return titleize(row.source);
    case 'pipedrive':
      return getPipedriveText(row);
    case 'documents': {
      const count = Number(row.document_count || 0);
      return `${count} document${count === 1 ? '' : 's'}`;
    }
    case 'owner':
      return row.assigned_owner_email || '-';
    case 'next_action':
      return row.next_action_label || formatDate(row.next_action_at);
    case 'last_inbound':
      return formatDate(row.last_inbound_contact_at);
    case 'round':
      return row.follow_up_round || 0;
    case 'review':
      return row.staff_review_required ? 'Review' : 'No';
    case 'class_action':
      return row.potential_class_action ? 'Potential Class Action' : 'Not Tagged';
    case 'decision':
      return getDecisionText(row);
    case 'created':
      return formatDate(row.created_at);
    case 'updated':
      return formatDate(row.updated_at);
    default:
      return '';
  }
}



export function getLeadActivePipelineWorkCount(lead) {
  const activeWork = lead?.active_pipeline_work || {};
  return Number(
    lead?.active_pipeline_work_count ??
    activeWork.total ??
    (
      Number(activeWork.pipeline_runs || 0) +
      Number(activeWork.stage_runs || 0) +
      Number(activeWork.agent_runs || 0)
    )
  ) || 0;
}



export function hasLeadActivePipelineWork(lead) {
  return Boolean(lead?.has_active_pipeline_work) || getLeadActivePipelineWorkCount(lead) > 0;
}



export function getPipelineQueueState(lead) {
  const state = lead?.workflow_summary?.state;
  if (['waiting_on_prospect', 'running', 'blocked_staff_review', 'failed'].includes(state)) return state;
  return '';
}



export function hasLeadRunningOrFailedPipelineState(lead) {
  return ['running', 'failed'].includes(getPipelineQueueState(lead));
}



export function applyRunRemainingResultState(lead, result) {
  const status = String(result?.status || '').toLowerCase();
  if (!lead || !status) return lead;
  if (!RUN_REMAINING_ACTIVE_RESULT_STATUSES.has(status)) {
    return {
      ...lead,
      has_active_pipeline_work: false,
      active_pipeline_work_count: 0,
      active_pipeline_work: EMPTY_ACTIVE_PIPELINE_WORK
    };
  }
  const activeWork = lead.active_pipeline_work || {};
  const pipelineRuns = Math.max(1, Number(activeWork.pipeline_runs || 0));
  const stageRuns = Number(activeWork.stage_runs || 0);
  const agentRuns = Number(activeWork.agent_runs || 0);
  const total = Math.max(
    1,
    Number(lead.active_pipeline_work_count || 0),
    Number(activeWork.total || 0),
    pipelineRuns + stageRuns + agentRuns
  );
  return {
    ...lead,
    has_active_pipeline_work: true,
    active_pipeline_work_count: total,
    active_pipeline_work: {
      ...activeWork,
      pipeline_runs: pipelineRuns,
      stage_runs: stageRuns,
      agent_runs: agentRuns,
      total
    }
  };
}



export function csvEscape(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}



export function buildCsv(rows, columns) {
  const header = columns.map((column) => csvEscape(column.label)).join(',');
  const body = rows.map((row) =>
  columns.map((column) => csvEscape(getCellText(row, column))).join(',')
  );
  return [header].concat(body).join('\n');
}



export function downloadCsv(csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const supportsObjectUrl = window.URL && typeof window.URL.createObjectURL === 'function';
  const url = supportsObjectUrl ?
  window.URL.createObjectURL(blob) :
  `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  const link = document.createElement('a');

  link.href = url;
  link.download = 'intake-table.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (supportsObjectUrl && typeof window.URL.revokeObjectURL === 'function') {
    window.URL.revokeObjectURL(url);
  }
}



export function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}



export function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to persist ${key}:`, error);
  }
}



export function toPositiveInteger(value, fallback) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : fallback;
}



export function normalizeMultiSelectFilterValue(value) {
  const rawValues = Array.isArray(value) ? value : String(value || '').split(',');
  return rawValues
  .map((item) => String(item || '').trim())
  .filter((item, index, values) => item && item !== 'all' && values.indexOf(item) === index);
}



export function normalizeStoredFilters(filters) {
  if (!filters || typeof filters !== 'object') {
    return { ...DEFAULT_FILTERS };
  }

  return Object.keys(DEFAULT_FILTERS).reduce(

  (normalized, key) => {
    const value = filters[key];
    if (value == null) return normalized;
    if (MULTI_SELECT_FILTER_FIELDS.has(key)) {
      return {
        ...normalized,
        [key]: normalizeMultiSelectFilterValue(value)
      };
    }
    return {
      ...normalized,
      [key]: String(value)
    };
  }, { ...DEFAULT_FILTERS });
}



export function normalizeTableState(value, storedVersion = TABLE_STATE_VERSION) {
  const state = value && typeof value === 'object' ? value : {};
  const pageSize = Number(state.pageSize);
  let sortField = SORT_FIELDS.has(state.sortField) ? state.sortField : DEFAULT_TABLE_STATE.sortField;
  if (storedVersion !== TABLE_STATE_VERSION && sortField === 'updated_at' && Number(state.sortOrder) !== 1) {
    sortField = LEAD_RECEIVED_SORT_FIELD;
  }

  return {
    filters: normalizeStoredFilters(state.filters),
    page: toPositiveInteger(state.page, DEFAULT_TABLE_STATE.page),
    pageSize: ROWS_PER_PAGE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_TABLE_STATE.pageSize,
    sortField,
    sortOrder: Number(state.sortOrder) === 1 ? 1 : -1
  };
}



export function readTableState() {
  let storedVersion = TABLE_STATE_VERSION;
  try {
    storedVersion = localStorage.getItem(TABLE_STATE_VERSION_KEY) || '';
  } catch {
    storedVersion = TABLE_STATE_VERSION;
  }
  return normalizeTableState(readJson(TABLE_STATE_KEY, DEFAULT_TABLE_STATE), storedVersion);
}



export function isActiveFilterValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value && value !== 'all');
}



export function serializeFilterParam(value) {
  if (Array.isArray(value)) return value.join(',');
  return value;
}



export function getLeadLabel(lead) {
  return lead?.prospect_name || lead?.lead_title || `Lead #${lead?.id || ''}`;
}



export function getDefendantLabel(lead) {
  return lead?.defendant_name_raw || lead?.defendant_name_normalized || 'No defendant';
}



export function getStateLabel(lead) {
  return lead?.state_client || lead?.state_defendant || lead?.state_events || '';
}



export function getNextActionLabel(lead) {
  return lead?.next_action_label || (lead?.next_action_at ? formatDate(lead.next_action_at) : 'No next action');
}



export function getLeadMetaLine(lead) {
  return [
  titleize(lead?.category),
  getStateLabel(lead),
  lead?.source ? titleize(lead.source) : '',
  lead?.assigned_owner_email || 'Unassigned']
  .filter((item) => item && item !== '-')
  .join(' / ');
}



export function getDocumentLabel(lead) {
  const documentCount = Number(lead?.document_count || 0);
  const pendingCount = Number(lead?.pending_document_review_count || 0);
  if (pendingCount > 0) return `${documentCount} docs / ${pendingCount} pending`;
  return `${documentCount} docs`;
}



export function getLeadFlagItems(lead) {
  const items = [];
  const pipelineState = getPipelineQueueState(lead);
  const stageLabel = getLeadStageLabel(lead);
  const documentCount = Number(lead?.document_count || 0);
  const pendingDocumentCount = Number(lead?.pending_document_review_count || 0);
  const hasRunningWork = hasLeadActivePipelineWork(lead) || pipelineState === 'running';
  const needsStaffReview = Boolean(lead?.staff_review_required) ||
    lead?.status === 'needs_staff_review' ||
    pipelineState === 'blocked_staff_review';

  if (pipelineState === 'failed' || lead?.status === 'error') {
    items.push({ key: 'workflow', label: 'Failed', className: 'intake-badge--danger' });
  } else if (pipelineState === 'waiting_on_prospect' || lead?.status === 'waiting_on_prospect') {
    items.push({ key: 'workflow', label: 'Waiting on Prospect', className: 'intake-badge--info' });
  } else if (hasRunningWork) {
    items.push({ key: 'workflow', label: 'Running', className: 'intake-badge--warning' });
  } else if (needsStaffReview) {
    items.push({ key: 'workflow', label: 'Needs Staff Review', className: 'intake-badge--danger' });
  } else if (lead?.status === 'ready_for_decision') {
    items.push({ key: 'workflow', label: 'Ready for Decision', className: 'intake-badge--success' });
  } else if (['new', 'processing'].includes(lead?.status) && lead?.current_stage) {
    items.push({ key: 'workflow', label: stageLabel, className: getStageClass(lead.current_stage) });
  } else {
    items.push({ key: 'workflow', label: titleize(lead?.status), className: getStatusClass(lead?.status) });
  }

  const primaryLabel = items[0]?.label;
  if (
    lead?.current_stage &&
    !['decision', 'conversion'].includes(lead.current_stage) &&
    primaryLabel !== stageLabel
  ) {
    items.push({ key: 'stage', label: stageLabel, className: getStageClass(lead.current_stage) });
  }

  if (lead?.duplicate_review_status && lead.duplicate_review_status !== 'not_duplicate') {
    items.push({
      key: 'dedupe',
      label: getDuplicateStatusLabel(lead),
      className: getDuplicateStatusClass(lead.duplicate_review_status)
    });
  }
  if (documentCount > 0) {
    items.push({
      key: 'documents',
      label: getDocumentLabel(lead),
      className: pendingDocumentCount > 0 ? 'intake-badge--warning' : 'intake-badge--info'
    });
  }
  if (lead?.ai_processing_locked) {
    items.push({ key: 'ai_lock', label: 'AI Locked', className: 'intake-badge--warning' });
  }
  if (lead?.potential_class_action) {
    items.push({ key: 'class_action', label: 'Class Action', className: 'intake-badge--violet' });
  }
  if (lead?.promotion_batch?.status) {
    items.push({
      key: 'promotion_batch',
      label: getPromotionBatchStatusLabel(lead.promotion_batch),
      className: getPromotionBatchStatusClass(lead.promotion_batch)
    });
  }
  return items;
}



export function isReadyForDecisionLead(lead) {
  return lead?.status === 'ready_for_decision' || lead?.current_stage === 'decision';
}



export function isWaitingLead(lead) {
  return lead?.status === 'waiting_on_prospect';
}



export function isRunningOrErrorLead(lead) {
  return lead?.status === 'error' || hasLeadActivePipelineWork(lead) || hasLeadRunningOrFailedPipelineState(lead);
}



export function needsStaffReviewLead(lead) {
  return Boolean(lead?.staff_review_required || lead?.status === 'needs_staff_review');
}



export function needsAttentionLead(lead) {
  const dueAt = lead?.next_action_at ? new Date(lead.next_action_at).getTime() : null;
  const due = Number.isFinite(dueAt) && dueAt <= Date.now();
  return Boolean(
    needsStaffReviewLead(lead) ||
    lead?.status === 'error' ||
    Number(lead?.pending_document_review_count || 0) > 0 ||
    due
  );
}



export function getInboxGroupKey(lead) {
  if (lead?.staff_review_required || lead?.status === 'needs_staff_review') return 'needs_review';
  if (['likely_duplicate', 'possible_duplicate'].includes(lead?.duplicate_review_status)) return 'duplicate_review';
  if (isReadyForDecisionLead(lead)) return 'ready_for_decision';
  if (Number(lead?.pending_document_review_count || 0) > 0) return 'documents';
  if (isWaitingLead(lead)) return 'waiting';
  if (isRunningOrErrorLead(lead)) return 'running';
  return 'other';
}



export function groupLeadsByInbox(leads) {
  const groups = [
  { key: 'needs_review', label: 'Needs Review', leads: [] },
  { key: 'duplicate_review', label: 'Duplicate Review', leads: [] },
  { key: 'ready_for_decision', label: 'Ready for Decision', leads: [] },
  { key: 'documents', label: 'Documents', leads: [] },
  { key: 'waiting', label: 'Waiting', leads: [] },
  { key: 'running', label: 'Running / Error', leads: [] },
  { key: 'other', label: 'Other', leads: [] }];
  const groupMap = groups.reduce((map, group) => ({ ...map, [group.key]: group }), {});
  (leads || []).forEach((lead) => {
    groupMap[getInboxGroupKey(lead)].leads.push(lead);
  });
  return groups;
}



export function groupLeadsByStage(leads) {
  const stageMap = new Map();
  (leads || []).forEach((lead) => {
    const key = lead.current_stage || 'unknown';
    if (!stageMap.has(key)) {
      stageMap.set(key, {
        key,
        label: titleize(key),
        leads: []
      });
    }
    stageMap.get(key).leads.push(lead);
  });
  return Array.from(stageMap.values());
}



export function getPipelineVersionLabel(version) {
  if (!version) return 'Pipeline';
  const versionPart = version.version_number ? `v${version.version_number}` : version.id ? `#${version.id}` : '';
  return [versionPart, version.name].filter(Boolean).join(' - ') || 'Pipeline';
}



export function getRunSortValue(run) {
  const timestamp = run?.completed_at || run?.updated_at || run?.started_at || run?.created_at;
  const parsed = timestamp ? Date.parse(timestamp) : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : Number(run?.id || 0);
}



export function getLatestRun(runs = []) {
  return [...runs].sort((left, right) => getRunSortValue(right) - getRunSortValue(left))[0] || null;
}



export function isClearedRun(run) {
  return CLEARED_STATUSES.has(String(run?.status || '').toLowerCase());
}



export function getStagePreviewState(stage, run, lead) {
  const status = String(run?.status || '').toLowerCase();
  if (FAILED_RUN_STATUSES.has(status)) {
    return { label: titleize(status), tone: 'danger', icon: 'pi pi-times-circle' };
  }
  if (REVIEW_RUN_STATUSES.has(status)) {
    return { label: titleize(status), tone: 'warning', icon: 'pi pi-exclamation-circle' };
  }
  if (ACTIVE_STATUSES.has(status)) {
    return { label: titleize(status), tone: 'active', icon: 'pi pi-spin pi-spinner' };
  }
  if (COMPLETE_STATUSES.has(status)) {
    return { label: titleize(status), tone: 'success', icon: 'pi pi-check-circle' };
  }
  if (status === 'skipped') {
    return { label: 'Skipped', tone: 'muted', icon: 'pi pi-forward' };
  }
  if (stage?.key && lead?.current_stage === stage.key) {
    return { label: 'Current', tone: 'current', icon: 'pi pi-map-marker' };
  }
  return { label: 'Not started', tone: 'neutral', icon: 'pi pi-circle' };
}



export function buildPreviewPipelineStages(payload, lead) {
  const pipelinePayload = payload?.pipeline_preview || payload || {};
  const configuredStages = pipelinePayload?.pipeline_stages || [];
  const stageRuns = pipelinePayload?.stage_runs || [];
  const visibleStageRuns = stageRuns.filter((run) => run?.stage_key && !isClearedRun(run));
  const agentRuns = pipelinePayload?.agent_runs || [];
  const currentStageKey = lead?.current_stage || '';
  const stagesByKey = new Map();
  configuredStages.forEach((stage, index) => {
    if (!stage?.key) return;
    stagesByKey.set(stage.key, {
      ...stage,
      order: Number.isFinite(Number(stage.order)) ? Number(stage.order) : index
    });
  });
  visibleStageRuns.forEach((run, index) => {
    if (!run?.stage_key) return;
    const configured = stagesByKey.get(run.stage_key);
    stagesByKey.set(run.stage_key, {
      ...configured,
      key: run.stage_key,
      label: configured?.label || run.stage_label || titleize(run.stage_key),
      description: configured?.description || '',
      order: Number.isFinite(Number(configured?.order)) ? Number(configured.order) : configuredStages.length + index
    });
  });
  if (currentStageKey && !stagesByKey.has(currentStageKey)) {
    stagesByKey.set(currentStageKey, {
      key: currentStageKey,
      label: titleize(currentStageKey),
      description: '',
      order: configuredStages.length + stageRuns.length
    });
  }
  const stageKeysWithRuns = new Set(visibleStageRuns.map((run) => run?.stage_key).filter(Boolean));
  const sortedStages = Array.from(stagesByKey.values())
  .filter((stage) => stageKeysWithRuns.has(stage.key) || stage.key === currentStageKey)
  .sort((left, right) => Number(left.order || 0) - Number(right.order || 0));

  return sortedStages.map(

  (stage) => {
    const runsForStage = visibleStageRuns.filter((run) => run.stage_key === stage.key);
    const latestRun = getLatestRun(runsForStage);
    const agentsForStage = latestRun ?
      agentRuns.filter((run) => run.stage_run_id === latestRun.id) :
      [];
    return {
      stage,
      latestRun,
      state: getStagePreviewState(stage, latestRun, lead),
      agentCount: agentsForStage.length,
      activeAgentCount: agentsForStage.filter((run) => ACTIVE_STATUSES.has(String(run?.status || '').toLowerCase())).length,
      failedAgentCount: agentsForStage.filter((run) => FAILED_RUN_STATUSES.has(String(run?.status || '').toLowerCase())).length
    };
  });
}



export function getPreviewCurrentStage(payload, lead) {
  const currentStageKey = lead?.current_stage || '';
  if (!currentStageKey) return null;
  return buildPreviewPipelineStages(payload, lead).find((item) => item.stage.key === currentStageKey) || {
    stage: {
      key: currentStageKey,
      label: titleize(currentStageKey),
      description: ''
    },
    latestRun: null,
    state: getStagePreviewState({ key: currentStageKey }, null, lead),
    agentCount: 0,
    activeAgentCount: 0,
    failedAgentCount: 0
  };
}



export function hasPreviewActivePipelineWork(payload, lead) {
  if (hasLeadActivePipelineWork(lead)) return true;
  if (payload?.action_state?.has_active_pipeline_work) return true;
  const pipelinePayload = payload?.pipeline_preview || payload || {};
  const activeRun =

  (run) => ACTIVE_STATUSES.has(String(run?.status || '').toLowerCase());
  return Boolean(payload) && [
    ...(pipelinePayload.pipeline_runs || []),
    ...(pipelinePayload.stage_runs || []),
    ...(pipelinePayload.agent_runs || [])
  ].some(activeRun);
}
